import React from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import _ from 'lodash';
import { OBSIDIAN } from '../../../constants/obsidianDashboardTheme';
import { LoadingOverlay, NoRowsOverlay } from '../../Tables/TableOverlay';
import { obsidianTableHeaderLabelSx, obsidianTableRowSx } from './obsidianTableTheme';

type MuiLikeCol = {
    field: string;
    width?: number;
    flex?: number;
    align?: 'left' | 'right' | 'center';
    headerAlign?: 'left' | 'right' | 'center';
    renderHeader: () => React.ReactNode;
    renderCell: (params: any) => React.ReactNode;
};

export interface ObsidianDashboardTableBodyProps {
    tableColumns: MuiLikeCol[];
    rows: any[];
    isLoading: boolean;
    headerHeight?: number;
    rowMinHeight?: number;
    textSx?: SxProps<Theme>;
    emptyOverlay?: React.ElementType;
    dimmed?: boolean;
}

function columnFlexWeights(cols: MuiLikeCol[]): number[] {
    return cols.map(c => {
        if (typeof c.width === 'number' && c.width > 0) return c.width;
        if (typeof c.flex === 'number' && c.flex > 0) return 100 * c.flex;
        return 120;
    });
}

function justify(align?: string) {
    if (align === 'right') return 'flex-end';
    if (align === 'center') return 'center';
    return 'flex-start';
}

function buildMockParams(row: any, col: MuiLikeCol, allRows: any[]) {
    return {
        row,
        id: row._id,
        field: col.field,
        value: _.get(row, col.field),
        api: { getAllRowIds: () => allRows.map(r => r._id) },
        colDef: col,
    };
}

export const ObsidianDashboardTableBody: React.FC<ObsidianDashboardTableBodyProps> = ({
    tableColumns,
    rows,
    isLoading,
    rowMinHeight = 64,
    textSx,
    emptyOverlay: EmptyOverlayProp,
    dimmed = false,
}) => {
    const weights = columnFlexWeights(tableColumns);
    const EmptyCmp = EmptyOverlayProp || NoRowsOverlay;
    const typographyCellSx: SystemStyleObject<Theme> =
        textSx != null && typeof textSx === 'object' && !Array.isArray(textSx)
            ? (textSx as SystemStyleObject<Theme>)
            : {};

    if (!tableColumns.length) return null;

    if (isLoading && !rows.length) {
        return (
            <Box
                sx={{
                    minHeight: 400,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 4,
                    bgcolor: OBSIDIAN.surfaceContainerLow,
                }}>
                <LoadingOverlay />
            </Box>
        );
    }

    if (!isLoading && !rows.length) {
        return (
            <Box
                sx={{
                    minHeight: 280,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 4,
                    bgcolor: OBSIDIAN.surfaceContainerLow,
                }}>
                <EmptyCmp />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                width: '100%',
                opacity: dimmed ? 0.5 : 1,
                transition: 'opacity 0.2s ease',
            }}>
            <Box
                sx={{
                    display: { xs: 'none', lg: 'flex' },
                    gap: 2,
                    alignItems: 'center',
                    px: 3,
                    py: 1,
                }}>
                {tableColumns.map((col, i) => (
                    <Box
                        key={col.field || `hdr-${i}`}
                        sx={{
                            flex: `${weights[i]} 1 0px`,
                            minWidth: 0,
                            display: 'flex',
                            justifyContent: justify(col.headerAlign || col.align),
                            ...obsidianTableHeaderLabelSx,
                        }}>
                        {col.renderHeader()}
                    </Box>
                ))}
            </Box>

            <Box sx={{ overflowX: 'auto', width: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: { xs: 720, lg: '100%' } }}>
                    {rows.map((row, ri) => (
                        <Box
                            key={row._id ?? `row-${ri}`}
                            sx={{
                                ...obsidianTableRowSx,
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center',
                                minHeight: rowMinHeight,
                            }}>
                            {tableColumns.map((col, i) => (
                                <Box
                                    key={col.field || `cell-${i}`}
                                    sx={{
                                        flex: `${weights[i]} 1 0px`,
                                        minWidth: 0,
                                        display: 'flex',
                                        justifyContent: justify(col.align),
                                        alignItems: 'center',
                                        '& .MuiTypography-root': typographyCellSx,
                                    }}>
                                    {col.renderCell(buildMockParams(row, col, rows))}
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};
